import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarClock, Plus, Send, Trash2 } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import {
  DASHBOARD_SCHEDULE_WEEKDAYS,
  isValidScheduleEmail,
  isValidScheduleTime,
  parseScheduleRecipients,
} from "@/features/dash/utils/dashboard-schedule"
import { api } from "@/lib/api-client"

type EmailSchedule = {
  id: string
  frequency: "daily" | "weekly"
  time_of_day: string
  weekday?: number
  recipients: string[]
  enabled: boolean
  created_at: string
  last_run_at?: string
  last_error?: string | null
}

type DashboardScheduleDialogProps = {
  dashboardId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Configure scheduled email snapshots for a dashboard — the killer
 * Power BI Pro feature. Owners/admins add schedules (daily / weekly,
 * with recipients), trigger immediate test sends, and remove old
 * entries. The cron-firing worker reads the same Cosmos records and
 * dispatches at the configured time; this UI is the only management
 * surface admins ever need to touch.
 */
export function DashboardScheduleDialog({
  dashboardId,
  open,
  onOpenChange,
}: DashboardScheduleDialogProps) {
  const { i18n, t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = ["dashboard-schedules", dashboardId] as const

  const { data: schedules = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const resp = await api.get<unknown, { schedules: EmailSchedule[] }>(
        `/dashboards/${dashboardId}/schedules`
      )
      return resp.schedules
    },
    enabled: open && !!dashboardId,
    staleTime: 1000 * 30,
  })

  const [frequency, setFrequency] = useState<"daily" | "weekly">("daily")
  const [timeOfDay, setTimeOfDay] = useState("08:00")
  const [weekday, setWeekday] = useState(0)
  const [recipientsRaw, setRecipientsRaw] = useState("")
  const [scheduleToDelete, setScheduleToDelete] = useState<EmailSchedule | null>(null)

  const recipients = parseScheduleRecipients(recipientsRaw)
  const hasInvalidRecipients = recipients.some((recipient) => !isValidScheduleEmail(recipient))
  const hasInvalidTime = timeOfDay.length > 0 && !isValidScheduleTime(timeOfDay)

  const createMutation = useMutation({
    mutationFn: () =>
      api.post<unknown, { schedule: EmailSchedule }>(`/dashboards/${dashboardId}/schedules`, {
        frequency,
        time_of_day: timeOfDay,
        weekday: frequency === "weekly" ? weekday : undefined,
        recipients,
        enabled: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      toast.success(t("dash.schedules.created", { defaultValue: "Schedule saved" }))
      setRecipientsRaw("")
    },
    onError: (err: Error) => {
      toast.error(t("dash.schedules.create_error", { defaultValue: "Could not save" }), {
        description: err.message,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      api.delete<unknown, { success: boolean }>(
        `/dashboards/${dashboardId}/schedules/${scheduleId}`
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      setScheduleToDelete(null)
    },
    onError: (err: Error) => {
      toast.error(t("dash.schedules.delete_error", { defaultValue: "Could not delete" }), {
        description: err.message,
      })
    },
  })

  const sendNowMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      api.post<unknown, { success: boolean }>(
        `/dashboards/${dashboardId}/schedules/${scheduleId}/send-now`,
        {}
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      toast.success(t("dash.schedules.sent", { defaultValue: "Snapshot delivered" }))
    },
    onError: (err: Error) => {
      const errorCode = (err as { response?: { data?: { code?: string } } }).response?.data?.code
      toast.error(t("dash.schedules.send_error", { defaultValue: "Send failed" }), {
        description:
          errorCode === "delegated_graph_token_unavailable"
            ? t("dash.schedules.graph_token_unavailable")
            : err.message,
      })
    },
  })

  const formValid = recipients.length > 0 && !hasInvalidRecipients && isValidScheduleTime(timeOfDay)

  const weekdayLabel = (day: number) =>
    t(`dash.schedules.weekdays.${DASHBOARD_SCHEDULE_WEEKDAYS[day] ?? "monday"}`)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="size-5" />
              {t("dash.schedules.dialog_title", { defaultValue: "Email snapshots" })}
            </DialogTitle>
            <DialogDescription>
              {t("dash.schedules.dialog_description", {
                defaultValue:
                  "Deliver a rendered snapshot of this dashboard on a schedule. Recipients receive the latest KPIs and tables as an HTML email.",
              })}
            </DialogDescription>
          </DialogHeader>

          {/* Create form */}
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">
                  {t("dash.schedules.frequency", { defaultValue: "Frequency" })}
                </Label>
                <Select
                  value={frequency}
                  onValueChange={(v) => setFrequency(v as "daily" | "weekly")}
                >
                  <SelectTrigger className="mt-1 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">
                      {t("dash.schedules.daily", { defaultValue: "Daily" })}
                    </SelectItem>
                    <SelectItem value="weekly">
                      {t("dash.schedules.weekly", { defaultValue: "Weekly" })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  {t("dash.schedules.time_of_day", { defaultValue: "Time (UTC)" })}
                </Label>
                <Input
                  aria-invalid={hasInvalidTime}
                  className="mt-1 h-8"
                  placeholder="08:00"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                />
                {hasInvalidTime && (
                  <p className="text-destructive mt-1 text-xs">
                    {t("dash.schedules.invalid_time")}
                  </p>
                )}
              </div>
            </div>
            {frequency === "weekly" && (
              <div>
                <Label className="text-xs">
                  {t("dash.schedules.weekday", { defaultValue: "Day" })}
                </Label>
                <Select value={String(weekday)} onValueChange={(v) => setWeekday(Number(v))}>
                  <SelectTrigger className="mt-1 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DASHBOARD_SCHEDULE_WEEKDAYS.map((day, idx) => (
                      <SelectItem key={day} value={String(idx)}>
                        {weekdayLabel(idx)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">
                {t("dash.schedules.recipients", {
                  defaultValue: "Recipients (comma or newline separated)",
                })}
              </Label>
              <Input
                aria-invalid={recipientsRaw.length > 0 && hasInvalidRecipients}
                className="mt-1 h-8"
                data-testid="schedule-recipients-input"
                placeholder="alice@example.com, bob@example.com"
                value={recipientsRaw}
                onChange={(e) => setRecipientsRaw(e.target.value)}
              />
              {recipientsRaw.length > 0 && hasInvalidRecipients && (
                <p className="text-destructive mt-1 text-xs">
                  {t("dash.schedules.invalid_recipients")}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="schedule-create-button"
                disabled={!formValid || createMutation.isPending}
                size="sm"
                onClick={() => createMutation.mutate()}
              >
                <Plus className="mr-1 size-3.5" />
                {t("dash.schedules.add", { defaultValue: "Add schedule" })}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Existing schedules */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("dash.schedules.existing", { defaultValue: "Active schedules" })}
            </Label>
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <Spinner className="size-4" />
              </div>
            )}
            {!isLoading && schedules.length === 0 && (
              <div className="text-muted-foreground py-2 text-sm">
                {t("dash.schedules.empty", {
                  defaultValue: "No scheduled snapshots yet.",
                })}
              </div>
            )}
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="flex items-center justify-between rounded-md border p-3"
                data-testid={`schedule-item-${schedule.id}`}
              >
                <div className="flex flex-col gap-0.5">
                  <div className="text-sm font-medium">
                    {schedule.frequency === "weekly"
                      ? t("dash.schedules.weekly_summary", {
                          day: weekdayLabel(schedule.weekday ?? 0),
                          time: schedule.time_of_day,
                        })
                      : t("dash.schedules.daily_summary", { time: schedule.time_of_day })}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t("dash.schedules.recipient_count", { count: schedule.recipients.length })}
                    {schedule.last_run_at &&
                      ` · ${t("dash.schedules.last_sent", {
                        time: new Date(schedule.last_run_at).toLocaleString(
                          i18n.resolvedLanguage ?? i18n.language
                        ),
                      })}`}
                  </div>
                  {schedule.last_error && (
                    <div className="text-destructive text-xs">{schedule.last_error}</div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    className="h-7 gap-1 text-xs"
                    disabled={sendNowMutation.isPending}
                    size="sm"
                    variant="outline"
                    onClick={() => sendNowMutation.mutate(schedule.id)}
                  >
                    <Send className="size-3" />
                    {t("dash.schedules.send_now", { defaultValue: "Send now" })}
                  </Button>
                  <Button
                    aria-label={t("dash.schedules.delete", { defaultValue: "Delete" })}
                    className="text-muted-foreground hover:text-destructive size-7 p-0"
                    disabled={deleteMutation.isPending}
                    size="icon"
                    variant="ghost"
                    onClick={() => setScheduleToDelete(schedule)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={scheduleToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setScheduleToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.schedules.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dash.schedules.delete_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (scheduleToDelete) deleteMutation.mutate(scheduleToDelete.id)
              }}
            >
              {t("dash.schedules.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
